import React, { useState } from "react"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Box, CircularProgress, Typography } from "@mui/material"
import { LockReset } from "@mui/icons-material"

const PasswordResetModal = ({ open, onClose, user, onResetPassword }) => {
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const handleReset = () => {
    setNewPassword("")
    setConfirmPassword("")
    setError(null)
    setSuccess(false)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const validatePassword = () => {
    if (!newPassword || !confirmPassword) {
      setError("Please fill in both password fields")
      return false
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long")
      return false
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match")
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    setError(null)
    setSuccess(false)

    if (!validatePassword()) {
      return
    }

    setLoading(true)

    try {
      // Use user.id (from Firestore doc.id) instead of user.uid
      await onResetPassword(user.id, newPassword)
      setSuccess(true)
      setNewPassword("")
      setConfirmPassword("")

      // Auto-close after 2 seconds on success
      setTimeout(() => {
        handleClose()
      }, 2000)
    } catch (err) {
      console.error("Password reset error:", err)
      setError(err.message || "Failed to reset password. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!user) return null

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LockReset color="primary" />
          <Typography variant="h6">Reset User Password</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* User Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>User:</strong> {user.displayName || user.email}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {user.email}
            </Typography>
          </Alert>

          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password reset successfully! The user will need to sign in again with the new password.
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Password Fields */}
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            disabled={loading || success}
            sx={{ mb: 2 }}
            helperText="Minimum 6 characters"
          />

          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            disabled={loading || success}
            onKeyPress={e => {
              if (e.key === "Enter" && !loading && !success) {
                handleSubmit()
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || success} startIcon={loading && <CircularProgress size={20} />}>
          {loading ? "Resetting..." : "Reset Password"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PasswordResetModal
